import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import useToastContext from 'hooks/useToastContext';
import { apiPost } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import EmpivoEvereeLogo from './components/EmpivoEvereeLogo';
import WizardStepper from './components/WizardStepper';
import { today } from './components/ui';

import StepCompanyInfo from './components/StepCompanyInfo';
import StepOwnersInfo from './components/StepOwnersInfo';
import StepDocuments from './components/StepDocuments';
import StepPayrollInfo from './components/StepPayrollInfo';
import StepAgreement from './components/StepAgreement';
import StepReview from './components/StepReview';

const STEPS = [
  { label: 'Company', sub: 'Subscription Agreement' },
  { label: 'Ownership', sub: 'Ownership & Control Person' },
  { label: 'Payroll', sub: 'Company Information' },
  { label: 'Documents', sub: 'Supporting Documents' },
  { label: 'Authorization', sub: 'Preauthorized Debits' },
  { label: 'Review', sub: 'Review & Submit' },
];
const TOTAL = STEPS.length;

// Fields validated before moving on from each step (step 4 = file checks, step 6 = submit)
const STEP_FIELDS = {
  1: ['companyInfo', 'agreement.subscription'],
  2: ['owners', 'ownersControl'],
  3: ['payrollInfo', 'companyInfo', 'agreement.certifyInitials'],
  5: [
    'agreement.bankDetails', 'agreement.signature', 'agreement.signerName',
    'agreement.signerTitle', 'agreement.date', 'agreement.isSigned',
  ],
};

const PayrollOnboardingWizard = () => {
  const {
    register, handleSubmit, watch, trigger, setValue, clearErrors, formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      owners: [{}, {}, {}, {}],
      payrollInfo: { states: [] },
      agreement: { date: today(), subscription: { date: today() } },
    },
  });
  const notification = useToastContext();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [docsAttempted, setDocsAttempted] = useState(false);
  const topRef = useRef(null);

  const [files, setFiles] = useState({ ownerIds: [], einLetter: null, signedContract: null });

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step, submitted]);

  const docsOk = files.ownerIds.length > 0 && !!files.einLetter && !!files.signedContract;

  const nextStep = async () => {
    if (step === 4) {
      if (!docsOk) { setDocsAttempted(true); return; }
    } else if (STEP_FIELDS[step]) {
      const ok = await trigger(STEP_FIELDS[step], { shouldFocus: true });
      if (!ok) return;
    }
    setStep((s) => Math.min(s + 1, TOTAL));
  };
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));
  const goToStep = (n) => n < step && setStep(n);

  const onSubmit = async (data) => {
    if (step < TOTAL) return;
    try {
      setLoading(true);
      const formData = new FormData();

      const owners = (data.owners || []).filter((o) => o && o.fullName);
      formData.append('companyInfo', JSON.stringify(data.companyInfo));
      formData.append('payrollInfo', JSON.stringify(data.payrollInfo));
      formData.append('agreement', JSON.stringify(data.agreement));
      formData.append('owners', JSON.stringify(owners));

      if (files.einLetter) formData.append('einLetter', files.einLetter);
      if (files.signedContract) formData.append('signedContract', files.signedContract);
      files.ownerIds.forEach((file) => formData.append('ownerIds', file));

      const res = await apiPost(apiPath.submitPayrollOnboarding, formData);

      if (res?.data?.success) {
        notification.success('Payroll onboarding submitted successfully!');
        setSubmitted(true);
      } else {
        notification.error(res?.data?.message || 'Submission failed');
      }
    } catch (err) {
      console.error(err);
      notification.error('An error occurred during submission.');
    } finally {
      setLoading(false);
    }
  };

  const shared = { register, errors, watch, setValue, clearErrors };

  return (
    <div className="min-h-screen bg-gray-100 px-3 py-6 md:py-10">
      <div ref={topRef} className="mx-auto max-w-5xl scroll-mt-4 overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5">
        <EmpivoEvereeLogo />

        {submitted ? (
          <div className="px-6 py-20 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700">
              <svg viewBox="0 0 20 20" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 10.5l3.5 3.5 7.5-8" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Submission received</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
              Thank you. Your completed form and documents have been submitted. Our team will review them and send them to Everee. We’ll be in touch if anything else is needed.
            </p>
          </div>
        ) : (
          <>
            <WizardStepper steps={STEPS} current={step} onStepClick={goToStep} />

            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName === 'INPUT' && step < TOTAL) e.preventDefault();
              }}
            >
              <div className="px-5 py-8 md:px-10">
                {step === 1 && <StepCompanyInfo {...shared} />}
                {step === 2 && <StepOwnersInfo {...shared} />}
                {step === 3 && <StepPayrollInfo {...shared} />}
                {step === 4 && <StepDocuments files={files} setFiles={setFiles} showErrors={docsAttempted} />}
                {step === 5 && <StepAgreement {...shared} />}
                {step === 6 && <StepReview watch={watch} files={files} goToStep={setStep} />}
              </div>

              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-5 py-4 md:px-10">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={step === 1}
                  className="rounded border border-gray-300 bg-white px-5 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:invisible"
                >
                  ← Back
                </button>

                <span className="hidden text-xs text-gray-400 sm:block">
                  <span className="text-red-600">*</span> Required field
                </span>

                {step < TOTAL ? (
                  <button
                    key="continue"
                    type="button"
                    onClick={nextStep}
                    className="rounded bg-blue-700 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    Continue →
                  </button>
                ) : (
                  <button
                    key="submit"
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={loading}
                    className="rounded bg-green-700 px-7 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-800 disabled:opacity-50"
                  >
                    {loading ? 'Submitting…' : 'Submit Payroll Setup'}
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PayrollOnboardingWizard;